import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Shield, RefreshCw, AlertTriangle, DollarSign, Clock, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const PaymentPolicies = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: "overview",
      title: "1. Visão Geral dos Pagamentos",
      icon: <CreditCard className="h-6 w-6 text-[#00B6F0]" />,
      content: `A Arena Pro Player oferece um sistema de pagamentos seguro e transparente para todas as transações na plataforma.

💳 Métodos de Pagamento Aceitos:
• Cartão de crédito (Visa, Mastercard, Elo, Amex)
• Cartão de débito
• PIX (pagamento instantâneo)
• Boleto bancário (vencimento em 3 dias úteis)

🔒 Segurança:
• Todos os pagamentos são processados através de parceiros certificados PCI DSS
• Dados sensíveis são criptografados e nunca armazenados em nossos servidores
• Autenticação 3D Secure para cartões quando disponível

💰 Moeda:
• Todas as transações são realizadas em Reais (BRL)
• Preços incluem todos os impostos aplicáveis`
    },
    {
      id: "pricing-structure",
      title: "2. Estrutura de Preços",
      icon: <DollarSign className="h-6 w-6 text-[#00B6F0]" />,
      content: `Como funcionam os preços na plataforma:

🎯 Para Jogadores:
• Preços definidos individualmente por cada mentor
• Valores variam conforme experiência e especialização do mentor
• Pacotes promocionais disponíveis (múltiplas sessões)
• Sem taxas adicionais para o jogador

💼 Para Mentores:
• Defina seus próprios preços por hora de mentoria
• A Arena Pro Player retém uma taxa de serviço de 15%
• Você recebe 85% do valor pago pelo aluno
• Pagamentos processados semanalmente

📊 Tipos de Serviços:
• Sessão individual (1 hora): R$ 50 - R$ 300 (média)
• Pacote 5 sessões: Desconto de 10-20%
• Pacote 10 sessões: Desconto de 15-25%
• Mentoria intensiva (4+ horas): Preços especiais

⚡ Promoções:
• Primeira sessão com desconto para novos alunos
• Descontos sazonais em datas especiais
• Cashback para usuários frequentes`
    },
    {
      id: "payment-processing",
      title: "3. Processamento de Pagamentos",
      icon: <Clock className="h-6 w-6 text-[#00B6F0]" />,
      content: `Entenda como e quando os pagamentos são processados:

⏰ Timing de Cobrança:
• PIX: Cobrança instantânea
• Cartão de crédito: Cobrança imediata na confirmação
• Cartão de débito: Cobrança imediata
• Boleto: Cobrança após compensação (1-3 dias úteis)

🔄 Quando o Pagamento é Liberado:
• Pagamento é retido até 24h após o término da mentoria
• Período permite resolução de disputas imediatas
• Liberação automática se não houver contestações
• Casos especiais podem ter liberação manual

💸 Repasse para Mentores:
• Processamento toda sexta-feira
• Mentores recebem via PIX ou transferência bancária
• Valor mínimo para saque: R$ 50
• Relatório detalhado enviado por e-mail

📧 Notificações:
• Confirmação imediata por e-mail e SMS
• Notificação de agendamento confirmado
• Lembrete 24h antes da mentoria
• Confirmação de pagamento liberado`
    },
    {
      id: "refund-policy",
      title: "4. Política de Reembolso",
      icon: <RefreshCw className="h-6 w-6 text-[#00B6F0]" />,
      content: `Nossa política de reembolso é justa e transparente:

✅ Reembolso Total (100%):
• Cancelamento pelo mentor com menos de 24h de antecedência
• Mentor não comparece à sessão agendada
• Problemas técnicos graves da plataforma
• Mentoria não atende às especificações acordadas

🔄 Reembolso Parcial (50%):
• Cancelamento pelo aluno com menos de 12h de antecedência
• Mentoria interrompida por problemas do aluno (internet, equipamentos)
• Mentoria encerrada prematuramente por comportamento inadequado do aluno

❌ Sem Reembolso:
• Cancelamento pelo aluno com mais de 24h de antecedência (crédito na conta)
• Aluno não comparece à sessão
• Desistência durante a mentoria sem justificativa técnica

⏱️ Prazos para Solicitar:
• Até 48h após o término da mentoria
• Casos excepcionais: até 7 dias com justificativa
• Disputas resolvidas em até 5 dias úteis

🔄 Processamento de Reembolsos:
• PIX/Débito: 1-2 dias úteis
• Cartão de crédito: 5-10 dias úteis (depende da operadora)
• Boleto: Apenas crédito na conta da plataforma`
    },
    {
      id: "dispute-resolution",
      title: "5. Resolução de Disputas",
      icon: <AlertTriangle className="h-6 w-6 text-[#00B6F0]" />,
      content: `Sistema justo para resolver conflitos entre usuários:

🚨 Como Abrir uma Disputa:
• Através do sistema de suporte na plataforma
• Via WhatsApp: (11) 99642-5204
• E-mail: disputas@arenaproplayer.com
• Formulário específico para disputas

📋 Informações Necessárias:
• Número da transação/agendamento
• Descrição detalhada do problema
• Evidências (prints, gravações, mensagens)
• Resultado esperado

🔍 Processo de Investigação:
• Análise inicial em até 24h
• Coleta de informações de ambas as partes
• Revisão de logs e gravações (quando disponíveis)
• Decisão imparcial baseada em evidências

⚖️ Possíveis Resultados:
• Reembolso total ou parcial
• Crédito adicional na conta
• Reagendamento sem custo
• Mediação entre as partes
• Suspensão de usuários em casos graves

📞 Mediação:
• Tentativa de acordo amigável primeiro
• Reunião virtual com mediador neutral
• Solução consensual quando possível
• Escalação apenas em casos necessários`
    },
    {
      id: "mentor-payments",
      title: "6. Pagamentos para Mentores",
      icon: <CheckCircle className="h-6 w-6 text-[#00B6F0]" />,
      content: `Como os mentores recebem seus pagamentos:

💰 Estrutura de Ganhos:
• 85% do valor pago pelo aluno
• 15% de taxa de serviço da plataforma
• Sem taxa adicional sobre transferências PIX
• Transferência bancária: taxa de R$ 2,50

📅 Cronograma de Pagamentos:
• Processamento toda sexta-feira
• Pagamentos de segunda a quinta entram no próximo ciclo
• Feriados podem atrasar processamento em 1 dia
• Mentores recebem no mesmo dia (PIX) ou próximo dia útil (TED)

🧾 Relatórios e Comprovantes:
• Relatório semanal detalhado por e-mail
• Dashboard com histórico completo
• Comprovantes de transferência disponíveis
• Relatório anual para declaração de renda

💳 Cadastro Bancário:
• Dados bancários obrigatórios para recebimento
• Validação de titularidade da conta
• Alterações processadas em até 48h
• Suporte para bancos digitais e tradicionais

📊 Acompanhamento:
• Saldo disponível em tempo real
• Previsão de próximos pagamentos
• Histórico de mentorias realizadas
• Performance e avaliações recebidas`
    },
    {
      id: "taxes-compliance",
      title: "7. Impostos e Conformidade",
      icon: <Shield className="h-6 w-6 text-[#00B6F0]" />,
      content: `Informações sobre questões fiscais e tributárias:

🧾 Para Jogadores:
• Valores pagos não são dedutíveis como despesa
• Não há emissão de nota fiscal para serviços educacionais
• Recibos disponíveis para controle pessoal
• Valores sujeitos a IOF quando aplicável

💼 Para Mentores:
• Renda deve ser declarada no Imposto de Renda
• Arena Pro Player é considerada pessoa física na legislação
• Retenção de IRRF apenas para valores acima de R$ 1.903,98/mês
• Relatórios mensais facilitam organização fiscal

📋 Documentação Fornecida:
• Relatório anual de ganhos para declaração
• Comprovantes detalhados de cada pagamento
• Histórico completo disponível na plataforma
• Suporte para questões fiscais básicas

⚖️ Conformidade Legal:
• Plataforma registrada e licenciada no Brasil
• Cumprimento da Lei Geral de Proteção de Dados (LGPD)
• Parceria apenas com processadores certificados
• Auditoria regular de processos financeiros

💡 Recomendações:
• Consulte um contador para dúvidas específicas
• Mantenha registros organizados
• Considere a abertura de MEI para valores altos
• Acompanhe mudanças na legislação tributária`
    },
    {
      id: "payment-security",
      title: "8. Segurança de Pagamentos",
      icon: <Shield className="h-6 w-6 text-[#00B6F0]" />,
      content: `Medidas implementadas para proteger suas transações:

🔒 Proteção de Dados:
• Criptografia SSL 256-bits em todas as transações
• Tokenização de dados de cartão
• Conformidade PCI DSS Level 1
• Monitoramento 24/7 de atividades suspeitas

🛡️ Prevenção de Fraudes:
• Sistema de análise de risco em tempo real
• Verificação de identidade para novos usuários
• Monitoramento de padrões anômalos
• Bloqueio automático de transações suspeitas

🔐 Autenticação:
• Autenticação 3D Secure (3DS) para cartões
• Verificação por SMS para transações grandes
• Biometria quando suportada pelo dispositivo
• Login com autenticação de dois fatores (2FA)

📱 Proteção Mobile:
• App com certificado de segurança
• Sessões criptografadas end-to-end
• Logout automático por inatividade
• Notificações push de segurança

🚨 Em Caso de Problema:
• Bloqueio imediato via suporte
• Investigação de transações não autorizadas
• Reversão de cobranças fraudulentas
• Suporte especializado em segurança`
    },
    {
      id: "international-users",
      title: "9. Usuários Internacionais",
      icon: <CreditCard className="h-6 w-6 text-[#00B6F0]" />,
      content: `Informações para usuários de outros países:

🌍 Países Aceitos:
• Atualmente limitado ao Brasil
• Expansão internacional planejada para 2024
• Cartões internacionais aceitos para brasileiros no exterior
• Verificação de residência pode ser necessária

💱 Conversão de Moeda:
• Todas as transações em Reais (BRL)
• Conversão feita pela operadora do cartão
• Taxa de câmbio e IOF aplicados pelo banco
• Preços podem variar devido a flutuações cambiais

🏦 Métodos de Pagamento:
• Cartões internacionais Visa e Mastercard aceitos
• PIX disponível apenas para contas brasileiras
• Boleto restrito a bancos brasileiros
• PayPal e outros métodos em avaliação

📄 Documentação:
• CPF brasileiro obrigatório para mentores
• Comprovante de residência pode ser solicitado
• Documentos aceitos em português, inglês ou espanhol
• Processo de verificação adicional pode ser necessário

🆘 Suporte:
• Atendimento em português e inglês
• Horários de suporte considerando fuso horário brasileiro
• Chat e e-mail disponíveis 24h
• WhatsApp apenas para números brasileiros`
    },
    {
      id: "contact-support",
      title: "10. Suporte Financeiro",
      icon: <CheckCircle className="h-6 w-6 text-[#00B6F0]" />,
      content: `Canais de suporte para questões de pagamento:

📞 Contato Direto:
• WhatsApp: (11) 99642-5204
• E-mail: financeiro@arenaproplayer.com
• Chat ao vivo na plataforma
• Telefone: (11) 3000-0000

⏰ Horários de Atendimento:
• Segunda a Sexta: 8h às 20h
• Sábado: 9h às 17h
• Domingo: 10h às 16h
• Feriados: Atendimento reduzido

🚨 Emergências Financeiras:
• Transações não autorizadas: Suporte imediato 24h
• Problemas com reembolsos: Prioridade alta
• Disputas urgentes: Resolução em até 24h
• Bloqueio de conta: Atendimento prioritário

📋 Informações Necessárias:
• Número da transação ou pedido
• E-mail cadastrado na conta
• Últimos 4 dígitos do cartão (se aplicável)
• Descrição detalhada do problema

📧 Recursos de Autoatendimento:
• FAQ completo na plataforma
• Tutoriais em vídeo
• Central de ajuda com busca
• Status de pagamentos em tempo real

💡 Dicas para Contato:
• Tenha sempre o número da transação em mãos
• Descreva o problema claramente
• Inclua capturas de tela quando possível
• Seja paciente - nossa equipe está aqui para ajudar!`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1117] via-[#0f141c] to-[#0c1117]">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Políticas de <span className="text-[#00B6F0]">Pagamento</span>
            </h1>
            <p className="text-gray-300 text-lg">
              Tudo que você precisa saber sobre pagamentos na Arena Pro Player
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-8"
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-[#00B6F0]/30 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                {section.icon}
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {section.title}
                </h2>
              </div>
              
              <div className="text-gray-300 leading-relaxed space-y-4">
                {section.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 p-6 bg-gradient-to-r from-green-500/10 to-[#00B6F0]/10 rounded-xl border border-green-500/30"
        >
          <h3 className="text-xl font-bold text-white mb-3">
            💳 Pagamentos Seguros e Transparentes
          </h3>
          <p className="text-gray-300 leading-relaxed">
            Na Arena Pro Player, sua segurança financeira é nossa prioridade. Utilizamos 
            as melhores práticas do mercado para garantir que suas transações sejam seguras, 
            rápidas e transparentes. Nossa equipe de suporte está sempre disponível para 
            esclarecer dúvidas e resolver questões relacionadas a pagamentos.
          </p>
        </motion.div>
      </div>
    </div>
  );
}; 